import { useState } from "react";

import type { Automation, AutomationTarget } from "@/lib/types";

import { useCreateAutomation, useUpdateAutomation } from "@/hooks/useAutomations";
import { useModels } from "@/hooks/useModels";
import { useStore } from "@/lib/store";

import { ScheduleInput } from "./ScheduleInput";
import { TargetSelector } from "./TargetSelector";

interface AutomationModalProps {
  automation?: Automation;
  onClose: () => void;
}

const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const TIMEZONE_OPTIONS = [
  "America/Sao_Paulo",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Tokyo",
  "UTC",
];

export function AutomationModal({ automation, onClose }: AutomationModalProps): JSX.Element {
  const isEdit = !!automation;
  const showToast = useStore((state) => state.showToast);
  const createAutomation = useCreateAutomation();
  const updateAutomation = useUpdateAutomation();
  const { data: modelsData } = useModels();

  const [name, setName] = useState(automation?.name ?? "");
  const [prompt, setPrompt] = useState(automation?.prompt ?? "");
  const [cron, setCron] = useState(automation?.schedule.cron ?? "0 9 * * 1-5");
  const [timezone, setTimezone] = useState(automation?.schedule.timezone ?? defaultTimezone);
  const [target, setTarget] = useState<AutomationTarget>(automation?.target ?? { source: "web" });
  const [modelId, setModelId] = useState<string | null>(automation?.modelId ?? null);

  const isPending = createAutomation.isPending || updateAutomation.isPending;

  // Add the user's current timezone to options if not already included
  const timezoneOptions = TIMEZONE_OPTIONS.includes(defaultTimezone)
    ? TIMEZONE_OPTIONS
    : [defaultTimezone, ...TIMEZONE_OPTIONS];

  async function handleSubmit() {
    if (!name.trim() || !prompt.trim() || isPending) return;

    try {
      if (isEdit) {
        await updateAutomation.mutateAsync({
          id: automation.id,
          name: name.trim(),
          prompt: prompt.trim(),
          schedule: { mode: "cron", cron, timezone },
          target,
          modelId,
        });
        showToast("Automation updated", "success");
      } else {
        await createAutomation.mutateAsync({
          name: name.trim(),
          prompt: prompt.trim(),
          schedule: { mode: "cron", cron, timezone },
          target,
          modelId,
        });
        showToast("Automation created", "success");
      }
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save automation";
      showToast(msg, "error");
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div role="presentation" className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="pointer-events-auto max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-cyber-border bg-cyber-surface p-5">
          <h3 className="mb-5 font-medium text-cyber-cyan">
            {isEdit ? "Edit automation" : "Create automation"}
          </h3>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="mb-1 block text-xs text-cyber-muted">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Check PostHog daily stats"
                className="w-full rounded border border-cyber-border bg-cyber-bg px-3 py-2 text-sm text-cyber-text focus:border-cyber-cyan/50 focus:outline-none"
                autoComplete="off"
                autoFocus
              />
            </div>

            {/* Prompt */}
            <div>
              <label className="mb-1 block text-xs text-cyber-muted">Prompt</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Check PostHog for conversion funnel trends and flag any significant changes"
                rows={3}
                className="w-full resize-none rounded border border-cyber-border bg-cyber-bg px-3 py-2 text-sm text-cyber-text focus:border-cyber-cyan/50 focus:outline-none"
              />
            </div>

            {/* Schedule */}
            <div>
              <label className="mb-1 block text-xs text-cyber-muted">Schedule</label>
              <ScheduleInput cron={cron} timezone={timezone} onChange={setCron} />
            </div>

            {/* Deliver to */}
            <div>
              <label className="mb-1 block text-xs text-cyber-muted">
                Deliver to <span className="text-cyber-muted/50">(optional)</span>
              </label>
              <TargetSelector value={target} onChange={setTarget} />
            </div>

            <div>
              <label className="mb-1 block text-xs text-cyber-muted">
                Model <span className="text-cyber-muted/50">(optional)</span>
              </label>
              <select
                value={modelId ?? ""}
                onChange={(event) => setModelId(event.target.value || null)}
                className="w-full rounded border border-cyber-border bg-cyber-bg px-3 py-2 text-sm text-cyber-text focus:border-cyber-cyan/50 focus:outline-none"
              >
                <option value="">Default</option>
                {modelsData?.models.map((model) => (
                  <option key={model.fullId} value={model.fullId}>
                    {model.providerName} &middot; {model.modelName}
                  </option>
                ))}
              </select>
            </div>

            {/* Timezone */}
            <div>
              <label className="mb-1 block text-xs text-cyber-muted">Timezone</label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full rounded border border-cyber-border bg-cyber-bg px-3 py-2 text-sm text-cyber-text focus:border-cyber-cyan/50 focus:outline-none"
              >
                {timezoneOptions.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-cyber-muted transition-all hover:text-cyber-text"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!name.trim() || !prompt.trim() || isPending}
              className="rounded border border-cyber-cyan/30 bg-cyber-cyan/10 px-3 py-1.5 text-sm text-cyber-cyan transition-all hover:border-cyber-cyan/50 hover:bg-cyber-cyan/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isPending ? (
                <>
                  <span className="animate-spin-glow mr-1 inline-block">◐</span>
                  {isEdit ? "Saving..." : "Creating..."}
                </>
              ) : isEdit ? (
                "Save"
              ) : (
                "Create"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
